import { create } from 'zustand';
import type { HouseConfig } from '@/model/schema';
import type { ScoreBreakdown } from '@/generator/scorer';

export interface ConfigState {
  /** Latest generated canonical config, or null before first generate. */
  config: HouseConfig | null;
  /** Generator score breakdown for the current config. */
  score: ScoreBreakdown | null;
  /** Rejection log entries, preserved for the debug panel. */
  rejections: Array<{ score: number; reasons: string[] }>;
  /** Monotonic counter — bump on every regenerate so effects can react. */
  generation: number;
  /**
   * JSON signature of the inputs (seed + constraints) that produced the
   * current result. Used by the single Generate button to decide whether a
   * click is a "re-run with the same inputs" (⇒ advance seed first) or a
   * "apply my new settings" (⇒ keep seed, regenerate).
   */
  lastSignature: string | null;

  setResult(
    result: { config: HouseConfig; score: ScoreBreakdown; rejections: ConfigState['rejections'] },
    signature: string,
  ): void;
  clear(): void;
}

export const useConfigStore = create<ConfigState>((set) => ({
  config: null,
  score: null,
  rejections: [],
  generation: 0,
  lastSignature: null,

  setResult: (result, signature) =>
    set((s) => ({
      config: result.config,
      score: result.score,
      rejections: result.rejections,
      generation: s.generation + 1,
      lastSignature: signature,
    })),

  clear: () =>
    set({ config: null, score: null, rejections: [], generation: 0, lastSignature: null }),
}));
